import { z } from "zod";

// All supported MIME types for file upload validation
const allowedMimeTypes = [
  // Documents
  "application/pdf",
  "application/msword", // DOC (legacy)
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
  "application/vnd.ms-excel", // XLS (legacy)
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
  "text/csv", // CSV
  "application/vnd.ms-powerpoint", // PPT (legacy)
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // PPTX
  "text/plain", // TXT
  "application/rtf", // RTF
  // OpenDocument
  "application/vnd.oasis.opendocument.text", // ODT
  "application/vnd.oasis.opendocument.spreadsheet", // ODS
  "application/vnd.oasis.opendocument.presentation", // ODP
  // Images
  "image/jpeg", // JPG/JPEG
  "image/png", // PNG
  "image/gif", // GIF
  "image/webp", // WEBP
  "image/svg+xml", // SVG
  // Video
  "video/mp4", // MP4
  "video/webm", // WEBM
  "video/quicktime", // MOV
  // Audio
  "audio/mpeg", // MP3
  "audio/wav", // WAV
  "audio/mp4", // M4A
  "audio/x-m4a", // M4A alternate
  // Archives
  "application/zip", // ZIP
  "application/x-zip-compressed", // ZIP alternate
  // eBooks
  "application/epub+zip", // EPUB
];

// Supported file extensions
const allowedExtensions = /\.(pdf|doc|docx|xls|xlsx|csv|ppt|pptx|txt|rtf|odt|ods|odp|jpg|jpeg|png|gif|webp|svg|mp4|webm|mov|mp3|wav|m4a|zip|epub)$/i;

// Sensitive data types that can be detected
export const sensitiveDataTypes = [
  'SSN',
  'DOB',
  'PHONE',
  'EMAIL',
  'CREDIT_CARD',
  'ADDRESS',
  'MEDICAL',
  'FINANCIAL',
  'POLICY_NUMBER',
  'DRIVERS_LICENSE',
] as const;

export const resourceSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional().default(""),
  file: z
    .any()
    .refine((file) => file instanceof File, { message: "File is required" })
    .refine((file) => file?.size <= 10 * 1024 * 1024, { message: "File size must be less than 10MB" })
    .refine(
      (file) => allowedMimeTypes.includes(file?.type) || allowedExtensions.test(file?.name),
      { message: "Please select a valid file (documents, images, video, audio, or archives)" }
    ),
  fileType: z.string().min(1, "File type is required"),
  carrier: z.string().min(1, "Carrier is required"),
  categoryId: z.string().min(1, "Category is required"),
  tags: z.array(z.string()).optional().default([]),
  scope: z.enum(['personal', 'agency-only', 'agency-and-below', 'agency-wide']).optional().default('personal'),
  // Sensitive info fields
  isSensitive: z.boolean().optional().default(false),
  sensitiveDataTypes: z.array(z.enum(sensitiveDataTypes)).optional().default([]),
});

export const resourceFormSchema = resourceSchema.omit({ file: true });